import { useState } from 'react'
import { DomainScope } from '../components/DomainScope'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import gsap from 'gsap'
import { api, ApiError, type CreatedApiKey } from '../lib/api'
import { useGsap } from '../lib/useGsap'

const ALL_SCOPES = [
  { id: 'messages:send', label: 'Send messages', note: 'The only scope most integrations need' },
  { id: 'messages:read', label: 'Read messages' },
  { id: 'events:read', label: 'Read delivery events' },
  { id: 'domains:read', label: 'Read domains' },
  { id: 'domains:write', label: 'Manage domains' },
  { id: 'suppressions:read', label: 'Read suppressions' },
  { id: 'suppressions:write', label: 'Manage suppressions' },
  { id: 'webhooks:write', label: 'Manage webhooks' },
]

export function ApiKeys() {
  const qc = useQueryClient()
  const [name, setName] = useState('')
  const [test, setTest] = useState(false)
  // Least privilege by default: most integrations only ever send.
  const [scopes, setScopes] = useState<string[]>(['messages:send'])
  const [allowedDomains, setAllowedDomains] = useState<string[]>([])
  const [ipAllowlist, setIpAllowlist] = useState('')
  const [created, setCreated] = useState<CreatedApiKey | null>(null)
  const [error, setError] = useState<ApiError | null>(null)

  const { data, isLoading } = useQuery({ queryKey: ['keys'], queryFn: api.keys.list })

  const create = useMutation({
    mutationFn: () => api.keys.create({
      name: name.trim(), scopes, test,
      allowedDomains: allowedDomains.length ? allowedDomains : undefined,
      ipAllowlist: ipAllowlist.split(/[\s,]+/).map(s => s.trim()).filter(Boolean),
    }),
    onSuccess: async (k) => {
      setCreated(k)
      setName('')
      setError(null)
      await qc.invalidateQueries({ queryKey: ['keys'] })
      // Reveal-once panel. This is the only moment the secret exists in a readable
      // form anywhere, so it gets the animation budget.
      gsap.fromTo('.reveal-once',
        { y: -8, opacity: 0 },
        { y: 0, opacity: 1, duration: 0.4, ease: 'power2.out' })
    },
    onError: (e) => setError(e as ApiError),
  })

  const revoke = useMutation({
    mutationFn: (id: string) => api.keys.revoke(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['keys'] }),
  })

  const ref = useGsap(({ el }) => {
    gsap.from(el.querySelectorAll('.key-row'), {
      y: 8, opacity: 0, duration: 0.28, stagger: 0.04, ease: 'power2.out',
    })
  }, [data?.data.length])

  function toggleScope(id: string) {
    setScopes(s => s.includes(id) ? s.filter(x => x !== id) : [...s, id])
  }

  return (
    <div ref={ref}>
      <h1>API keys</h1>
      <p className="lede">
        Keys authenticate your code. We store only a hash, so a key is shown once and can
        never be retrieved again — only revoked and replaced.
      </p>

      {created && (
        <div className="reveal-once card" style={{ marginBottom: 24, borderColor: 'var(--brand-500)' }}>
          <h2 style={{ marginTop: 0 }}>Copy this key now</h2>
          <p className="small" style={{ color: 'var(--danger)', fontWeight: 600 }}>
            {created.warning}
          </p>
          <div className="copyable">
            <span style={{ flex: 1 }}>{created.secret}</span>
            <button className="secondary" onClick={() => navigator.clipboard?.writeText(created.secret)}>
              Copy
            </button>
          </div>

          <h2>Try it</h2>
          <pre className="copyable" style={{ display: 'block', whiteSpace: 'pre-wrap', margin: 0 }}>
{`curl -X POST https://api.itsbingmybusiness.com/v1/messages \\
  -H "Authorization: Bearer ${created.secret}" \\
  -H "Content-Type: application/json" \\
  -d '{
    "from": "you@yourdomain.com",
    "to": ["someone@example.com"],
    "subject": "Hello",
    "text": "Sent through ItsBingMyBusiness."
  }'`}
          </pre>

          <button className="secondary" style={{ marginTop: 16 }} onClick={() => setCreated(null)}>
            I have saved it
          </button>
        </div>
      )}

      <div className="card" style={{ marginBottom: 24 }}>
        <form onSubmit={e => { e.preventDefault(); if (name.trim()) create.mutate() }}>
          <div className="field">
            <label htmlFor="kn">Key name</label>
            <input id="kn" placeholder="production-api" value={name}
                   onChange={e => setName(e.target.value)} />
            <div className="hint">
              Name it after where it will live, so you know which one is safe to revoke later.
            </div>
          </div>

          <div className="field">
            <label>Scopes</label>
            <div className="stack" style={{ gap: 6 }}>
              {ALL_SCOPES.map(s => (
                <label key={s.id} className="row small" style={{ fontWeight: 400 }}>
                  <input type="checkbox" style={{ width: 16, height: 16 }}
                         checked={scopes.includes(s.id)} onChange={() => toggleScope(s.id)} />
                  <span className="mono">{s.id}</span>
                  <span className="muted">— {s.label}</span>
                  {s.note && <span className="muted">({s.note})</span>}
                </label>
              ))}
            </div>
          </div>

          <DomainScope value={allowedDomains} onChange={setAllowedDomains} />

          <div className="field">
            <label htmlFor="ipa">IP allowlist <span className="muted">(optional)</span></label>
            <input id="ipa" placeholder="203.0.113.4, 198.51.100.0/24" value={ipAllowlist}
                   onChange={e => setIpAllowlist(e.target.value)} className="mono" />
            <div className="hint">
              Comma- or space-separated IPs / CIDRs. If set, this key only works from these
              addresses — a leaked key is useless from anywhere else.
            </div>
          </div>

          <div className="field">
            <label className="row small" style={{ fontWeight: 400 }}>
              <input type="checkbox" style={{ width: 16, height: 16 }}
                     checked={test} onChange={e => setTest(e.target.checked)} />
              <span>Test key (<span className="mono">sk_test_</span>) — sandbox only</span>
            </label>
          </div>

          {error && <div className="alert error">{error.message}</div>}

          <button type="submit" disabled={!name.trim() || scopes.length === 0 || create.isPending}>
            {create.isPending ? 'Creating…' : 'Create key'}
          </button>
        </form>
      </div>

      {isLoading && <div className="skeleton" style={{ height: 120 }} />}

      {!isLoading && data?.data.length === 0 && (
        <div className="empty">
          <p><strong>No keys yet.</strong></p>
          <p>Create one to start sending from your code.</p>
        </div>
      )}

      {data && data.data.length > 0 && (
        <div className="card scroll-x">
          <table>
            <thead>
              <tr>
                <th>Name</th><th>Prefix</th><th>Scopes</th>
                <th>Last used</th><th>Created</th><th />
              </tr>
            </thead>
            <tbody>
              {data.data.map(k => (
                <tr key={k.id} className="key-row" style={{ opacity: k.revoked_at ? 0.5 : 1 }}>
                  <td>
                    <div style={{ fontWeight: 600 }}>{k.name}</div>
                    {k.test && <span className="badge neutral">test</span>}
                    {k.revoked_at && <span className="badge failed">revoked</span>}
                  </td>
                  <td className="mono small">{k.prefix}…</td>
                  <td className="small muted mono" style={{ maxWidth: 240 }}>{k.scopes}</td>
                  <td className="small muted">
                    {/* The one field that tells you which key is safe to revoke. */}
                    {k.last_used_at
                      ? <>{new Date(k.last_used_at).toLocaleString()}<br />
                          <span className="mono">{k.last_used_ip}</span></>
                      : 'never'}
                  </td>
                  <td className="small muted">{new Date(k.created_at).toLocaleDateString()}</td>
                  <td>
                    {!k.revoked_at && (
                      <button className="ghost small"
                              onClick={() => confirm(`Revoke "${k.name}"? Any code using it stops working immediately.`)
                                && revoke.mutate(k.id)}>
                        Revoke
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
